import { Check, ChevronDown, Search, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { t } from '@/lib/i18n';

interface ComboOption {
  value: string;
  label: string;
  /** Extra text under the label (e.g. the lines a station is on); also searched. */
  hint?: string;
}

/** Lower case without accents, so "qender" finds "Qendër" and "c" finds "ç". */
export const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Searchable select for long lists. Type to filter, arrows + Enter to choose, Esc to close.
 * With `resetOnSelect` it works as an "add …" picker: the field empties after each choice.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = t('Search…'),
  emptyLabel,
  resetOnSelect = false,
  disabled,
  invalid,
  className = '',
}: {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  /** Offer an empty choice with this label (e.g. "All lines", "— none —"). */
  emptyLabel?: string;
  resetOnSelect?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const selected = options.find((o) => o.value === value);

  const shown = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    const matches = words.length
      ? options.filter((o) => {
          const text = fold(`${o.label} ${o.hint ?? ''}`);
          return words.every((w) => text.includes(w));
        })
      : options;
    const all = emptyLabel && !words.length ? [{ value: '', label: emptyLabel }, ...matches] : matches;
    return all.slice(0, 200);
  }, [options, query, emptyLabel]);

  useEffect(() => {
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (o: ComboOption | undefined) => {
    if (!o) return;
    onChange(o.value);
    setQuery('');
    if (resetOnSelect) input.current?.focus();
    else {
      setOpen(false);
      input.current?.blur();
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open) {
        e.preventDefault();
        choose(shown[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const display = open || resetOnSelect ? query : (selected?.label ?? '');
  return (
    <div className={`relative ${/\bw-/.test(className) ? '' : 'w-full'} ${className}`}>
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={input}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        value={display}
        placeholder={selected && !resetOnSelect ? selected.label : placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          setQuery('');
        }}
        onKeyDown={onKey}
        className={`h-9 w-full rounded-md border bg-white pr-14 pl-8 text-sm shadow-sm placeholder:text-slate-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 ${invalid ? 'border-red-400' : 'border-slate-300'}`}
      />
      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1 text-slate-400">
        {value && !resetOnSelect && emptyLabel !== undefined && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={t('Clear')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange('')}
            className="hover:text-slate-700"
          >
            <X className="size-4" />
          </button>
        )}
        <ChevronDown className="pointer-events-none size-4" />
      </div>
      {open && (
        <div
          ref={list}
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full min-w-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {shown.length === 0 && <div className="px-3 py-2 text-slate-500">{t('No matches')}</div>}
          {shown.map((o, i) => (
            <div
              key={o.value || '__empty'}
              id={`${listId}-${i}`}
              tabIndex={-1}
              role="option"
              aria-selected={o.value === value}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
              onKeyDown={(e) => e.key === 'Enter' && choose(o)}
              className={`flex cursor-pointer items-start gap-2 px-3 py-1.5 ${i === active ? 'bg-brand-50' : ''}`}
            >
              <Check
                className={`mt-0.5 size-4 shrink-0 ${o.value === value ? 'text-brand-600' : 'invisible'}`}
              />
              <span className="min-w-0">
                <span className={`block truncate ${o.value ? '' : 'text-slate-500'}`}>{o.label}</span>
                {o.hint && <span className="block truncate text-xs text-slate-500">{o.hint}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
